import { useEffect } from 'react';

// Every canonical URL points at the production site, whichever host served
// the page (www.ivisora.co, a preview, localhost).
export const SITE_ORIGIN = 'https://ivisora.co';

/** The canonical URL for a path: production origin, no query, no hash, no trailing slash. */
export function canonicalUrl(path) {
  let p = String(path || '/').split(/[?#]/)[0] || '/';
  if (p.length > 1) p = p.replace(/\/+$/, '');
  return `${SITE_ORIGIN}${p.startsWith('/') ? p : `/${p}`}`;
}

function setTag(selector, create, apply) {
  let node = document.head.querySelector(selector);
  const created = !node;
  if (created) {
    node = create();
    document.head.appendChild(node);
  }
  const restore = apply(node);
  return () => {
    if (created) node.remove();
    else restore();
  };
}

/**
 * Sets the tab title, meta description, canonical URL and robots directive
 * while a page is shown, and puts back what was there when it goes away.
 *
 *   options.path     the page's path — its canonical URL (indexable pages)
 *   options.noindex  ask search engines not to index the page (no canonical)
 */
export default function usePageMeta(title, description, options = {}) {
  const { path, noindex = false } = options;
  useEffect(() => {
    const previousTitle = document.title;
    document.title = /iVisora/.test(title) ? title : `${title} · iVisora`;
    const undo = [];

    if (description) {
      undo.push(setTag('meta[name="description"]', () => {
        const m = document.createElement('meta'); m.name = 'description'; return m;
      }, (m) => {
        const before = m.getAttribute('content');
        m.setAttribute('content', description);
        return () => { if (before === null) m.removeAttribute('content'); else m.setAttribute('content', before); };
      }));
    }
    if (noindex) {
      undo.push(setTag('meta[name="robots"]', () => {
        const m = document.createElement('meta'); m.name = 'robots'; return m;
      }, (m) => {
        const before = m.getAttribute('content');
        m.setAttribute('content', 'noindex');
        return () => { if (before === null) m.removeAttribute('content'); else m.setAttribute('content', before); };
      }));
    } else if (path) {
      undo.push(setTag('link[rel="canonical"]', () => {
        const l = document.createElement('link'); l.rel = 'canonical'; return l;
      }, (l) => {
        const before = l.getAttribute('href');
        l.setAttribute('href', canonicalUrl(path));
        return () => { if (before === null) l.removeAttribute('href'); else l.setAttribute('href', before); };
      }));
    }
    return () => {
      document.title = previousTitle;
      undo.reverse().forEach((fn) => fn());
    };
  }, [title, description, path, noindex]);
}
